import { parseDateRange } from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { TableFilterState } from '@/components/data-table/filters-v2';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { TimeOffRequestsFilter } from '../api/time-offs.api';
import {
  FILTER_KEY_DATE_RANGE,
  FILTER_KEY_REQUESTER_IDS,
  FILTER_KEY_STATUSES,
  FILTER_KEY_TIME_CATEGORY,
  FILTER_KEY_TYPES,
} from '../constants/time-offs-filter-keys';
import { TimeOffStatus } from '../types/time-off-status';
import { TimeOffTimeCategory } from '../types/time-off-time-category';

const toArray = (value: string | string[] | undefined): string[] => {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

export const mapTimeOffFilters = (filters: TableFilterState): TimeOffRequestsFilter => {
  const result: TimeOffRequestsFilter = {};

  const typeIds = toArray(filters[FILTER_KEY_TYPES])
    .map(v => Number(v))
    .filter(n => !Number.isNaN(n));
  if (typeIds.length > 0) result.typeIds = typeIds;

  const statuses = toArray(filters[FILTER_KEY_STATUSES]).filter((v): v is TimeOffStatus =>
    Object.values(TimeOffStatus).includes(v as TimeOffStatus)
  );
  if (statuses.length > 0) result.statuses = statuses;

  const requesterIds = toArray(filters[FILTER_KEY_REQUESTER_IDS])
    .map(v => Number(v))
    .filter(n => !Number.isNaN(n));
  if (requesterIds.length > 0) result.requesterIds = requesterIds;

  const timeCategoryValues = toArray(filters[FILTER_KEY_TIME_CATEGORY]);
  const timeCategory = timeCategoryValues[0];
  if (
    timeCategory &&
    Object.values(TimeOffTimeCategory).includes(timeCategory as TimeOffTimeCategory)
  ) {
    result.timeCategory = timeCategory as TimeOffTimeCategory;
  }

  const dateRangeValue = filters[FILTER_KEY_DATE_RANGE];
  const dateRangeString = typeof dateRangeValue === 'string' ? dateRangeValue : dateRangeValue?.[0];
  if (dateRangeString) {
    const parsed = parseDateRange(dateRangeString);
    if (parsed) {
      result.dateRange = {
        from: normalizeToStartOfDayUTC(parsed.startDate).toISOString(),
        to: normalizeToStartOfDayUTC(parsed.endDate).toISOString(),
      };
    }
  }

  return result;
};

export const buildTimeOffRequestsFilter = ({
  filters,
  currentUserId,
  canViewOtherTimeOffs,
}: {
  filters: TableFilterState;
  currentUserId: number;
  canViewOtherTimeOffs: boolean;
}): TimeOffRequestsFilter => {
  const mappedFilters = mapTimeOffFilters(filters);

  if (!canViewOtherTimeOffs || !mappedFilters.requesterIds?.length) {
    return { ...mappedFilters, requesterIds: [currentUserId] };
  }

  return mappedFilters;
};
